import React, { useState } from 'react';
import { JOY_TENNIS_BAODIAN, formatBaodianForWeChat } from '../core/baodian';
import { BaodianRule } from '../core/types';
import { tennisAudio } from '../core/audio';
import { useI18n } from '../core/i18n';
import {
  BookOpen,
  Search,
  Copy,
  Check,
  Tag,
  Sparkles,
  Flame,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

export const BaodianView: React.FC = () => {
  const { t } = useI18n();
  const [activeCategory, setActiveCategory] = useState<'all' | 'singles' | 'doubles' | 'serve_return' | 'mental'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(JOY_TENNIS_BAODIAN[0].id);

  const categories = [
    { id: 'all', label: '全部精选宝典' },
    { id: 'singles', label: '🎾 单打18条法则' },
    { id: 'doubles', label: '👥 双打制胜全书' },
    { id: 'serve_return', label: '⚡ 发球接发心法' },
    { id: 'mental', label: '🧘 心理抗压与内省' },
  ];

  const filteredRules = JOY_TENNIS_BAODIAN.filter((rule) => {
    if (activeCategory !== 'all' && rule.category !== activeCategory) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        rule.title.toLowerCase().includes(q) ||
        rule.titleEn.toLowerCase().includes(q) ||
        rule.formula.toLowerCase().includes(q) ||
        rule.summary.toLowerCase().includes(q) ||
        rule.tags.some((tag) => tag.toLowerCase().includes(q))
      );
    }
    return true;
  });

  const handleCopy = (rule: BaodianRule, e: React.MouseEvent) => {
    e.stopPropagation();
    const formatted = formatBaodianForWeChat(rule);
    navigator.clipboard.writeText(formatted);
    setCopiedId(rule.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
    tennisAudio.playHit('slice');
  };

  return (
    <div className="w-full max-w-5xl mx-auto p-3 sm:p-5 space-y-5">
      {/* Top Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-850 to-slate-900 p-5 rounded-3xl border border-slate-800 shadow-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500/20 to-rose-500/20 border border-amber-500/40 flex items-center justify-center text-amber-400">
            <BookOpen className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-black text-white flex items-center gap-2">
              <span>欢乐网球实战宝典库</span>
              <span className="text-[10px] bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full border border-amber-500/40 font-bold">
                Master Playbook
              </span>
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">
              汇聚沃德洛方向法则、双打雨刷封网、关键分大心脏心理学与微信群一键分享
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="搜索战术口诀、发球、双打..."
            className="w-full bg-slate-950 border border-slate-700/80 rounded-xl pl-9 pr-3 py-1.5 text-xs text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none"
          />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => {
              setActiveCategory(cat.id as any);
              tennisAudio.playHit('topspin');
            }}
            className={`px-3.5 py-1.5 rounded-xl border text-xs font-bold transition-all ${
              activeCategory === cat.id
                ? 'bg-amber-500 text-slate-950 border-amber-400 shadow-md'
                : 'bg-slate-900 border-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Rules List */}
      <div className="space-y-3.5">
        {filteredRules.map((rule) => {
          const isExpanded = expandedId === rule.id;
          const isCopied = copiedId === rule.id;

          return (
            <div
              key={rule.id}
              className={`rounded-2xl border transition-all overflow-hidden ${
                isExpanded
                  ? 'bg-slate-900/90 border-amber-500/40 shadow-xl'
                  : 'bg-slate-900/60 border-slate-800 hover:border-slate-700'
              }`}
            >
              {/* Header Accordion */}
              <div
                onClick={() => toggleExpand(rule.id)}
                className="p-4 flex items-center justify-between cursor-pointer"
              >
                <div className="space-y-1 flex-1 pr-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-black text-white">{rule.title}</span>
                    <span className="text-[10px] font-mono text-slate-400">{rule.titleEn}</span>
                  </div>
                  {/* Tactical Formula Rhyme */}
                  <div className="text-xs font-serif text-amber-300 font-bold tracking-wide">
                    「 {rule.formula} 」
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => handleCopy(rule, e)}
                    className="p-2 rounded-xl bg-slate-800/80 hover:bg-slate-750 text-slate-300 hover:text-amber-300 transition-colors"
                    title={t.wechatShare}
                  >
                    {isCopied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                  {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-5 pt-1 space-y-4 border-t border-slate-800/80 bg-slate-950/40 text-xs text-slate-300">
                  {/* Summary */}
                  <p className="leading-relaxed text-slate-200">{rule.summary}</p>

                  {/* Strategic Points */}
                  <div className="space-y-1.5">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-amber-400">
                      ⚡ 实战攻防心法 (Strategic Key Points)
                    </div>
                    <ul className="space-y-1 pl-1">
                      {rule.details.map((d, i) => (
                        <li key={i} className="flex items-start space-x-2">
                          <span className="text-amber-400 font-mono">•</span>
                          <span className="leading-relaxed">{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Drills */}
                  <div className="p-3 bg-slate-900 rounded-xl border border-slate-800">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-sky-400 mb-1">
                      🏋️ 特训拉练建议 (Prescribed Drills)
                    </div>
                    <div className="space-y-1">
                      {rule.drills.map((drill, idx) => (
                        <div key={idx} className="text-slate-300 flex items-center space-x-1.5">
                          <span className="text-sky-400 text-xs">✓</span>
                          <span>{drill}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {rule.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-slate-850 text-slate-400 border border-slate-800"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
